import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

const LIST_MAX_DEFAULT_PX = 320;
const CHROME_ESTIMATE_PX = 88;
const GAP_PX = 8;
const VIEWPORT_PAD_PX = 16;
const LIST_MIN_PX = 96;

export interface SpotlightSlashPanelLayout {
  readonly panelStyle: CSSProperties;
  readonly listMaxHeight: number;
}

export function measureSpotlightSlashPanelLayout(
  anchor: HTMLElement,
  placement: 'above' | 'below'
): SpotlightSlashPanelLayout {
  const rect = anchor.getBoundingClientRect();
  if (placement === 'above') {
    const availableAbove = rect.top - GAP_PX - VIEWPORT_PAD_PX;
    return {
      panelStyle: {
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + GAP_PX,
        zIndex: 100,
      },
      listMaxHeight: Math.min(
        LIST_MAX_DEFAULT_PX,
        Math.max(LIST_MIN_PX, availableAbove - CHROME_ESTIMATE_PX)
      ),
    };
  }

  const availableBelow = window.innerHeight - rect.bottom - GAP_PX - VIEWPORT_PAD_PX;
  return {
    panelStyle: {
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      top: rect.bottom + GAP_PX,
      zIndex: 100,
    },
    listMaxHeight: Math.min(
      LIST_MAX_DEFAULT_PX,
      Math.max(LIST_MIN_PX, availableBelow - CHROME_ESTIMATE_PX)
    ),
  };
}

export function useSpotlightSlashPanelLayout(
  anchorRef: RefObject<HTMLElement | null>,
  placement: 'above' | 'below',
  enabled: boolean,
  followMotion = false
): SpotlightSlashPanelLayout | null {
  const [layout, setLayout] = useState<SpotlightSlashPanelLayout | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setLayout(null);
      return;
    }

    let frame = 0;
    let lastBox = '';
    let observer: ResizeObserver | undefined;

    const readBox = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      return rect ? `${rect.top},${rect.bottom},${rect.left},${rect.width}` : '';
    };

    const update = () => {
      lastBox = readBox();
      const el = anchorRef.current;
      setLayout(el ? measureSpotlightSlashPanelLayout(el, placement) : null);
    };

    // Size changes (toolbar expand, editor wrap) are a ResizeObserver. Framer
    // `layout="position"` moves the box with a transform, which does not
    // resize — follow those frames only when the caller opts in.
    const followAnchor = () => {
      if (readBox() !== lastBox) update();
      frame = requestAnimationFrame(followAnchor);
    };

    update();
    const anchor = anchorRef.current;
    if (typeof ResizeObserver !== 'undefined' && anchor) {
      observer = new ResizeObserver(() => update());
      observer.observe(anchor);
    }
    if (followMotion) {
      frame = requestAnimationFrame(followAnchor);
    }
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef, placement, enabled, followMotion]);

  if (!enabled) return null;

  const anchor = anchorRef.current;
  if (!anchor) return layout;

  return layout ?? measureSpotlightSlashPanelLayout(anchor, placement);
}
